import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as Material from "@mui/material";

import "./index.scss";

import { Button, Text } from "@fragments";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { SlowMotionVideoRounded, SkipNextRounded, AccessTimeRounded, KeyboardArrowDownRounded } from "@mui/icons-material";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";

import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';

import api from "@helpers/api";

import Playlist from "@pages/Section/components/Playlist";
import SuggestionCard from "./components/SuggestionCard";


export default function CoursesBar({ info, handleCreateNew }) {
    const navigate = useNavigate();

    const sectionRef = useRef(null);
    const headerRef = useRef(null);

    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            if (!info?.id) return;
            setLoading(true);
            try {
                const { data } = await api.get(`community/${info.id}/sections`);
                if (cancelled) return;
                setSections(Array.isArray(data) ? data : []);
            } catch (e) {
                console.log(e);
                if (!cancelled) setSections([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => { cancelled = true; };
    }, [info?.id]);

    const pinnedSections = sections.filter((s) => s?.fixed_playlist);
    const otherSections = sections.filter((s) => !s?.fixed_playlist);

    function Course({ data }) {
        return (
            <div
                className="course-item"
                onClick={() => {
                    navigate("section/" + data.id);
                }}
            >
                <div className="banner-container bsf-9">
                    <img src={data.banner?.condensed} className="banner" />
                </div>

                <img src={data.banner?.condensed} className="banner-effect-on-hover" />

                <div className="info mt-2">
                    <div className="title-container csf-2">
                        <KeyboardDoubleArrowRightRoundedIcon className="arrow-icon" />
                        <h4 className="bold">{data.name}</h4>
                    </div>
                    <span className="description csf-6" numberOfLines={2}>
                        {data.description?.text}
                    </span>
                </div>
            </div>
        );
    }

    function PinnedSection({ section }) {
        function handleOpenContent(playlistInfo, index) {
            const sectionId = playlistInfo?.section ?? section.id;
            const contentId = playlistInfo?.contents?.[index]?.id;
            if (!contentId) return;
            navigate(`section/${sectionId}/${contentId}`);
        }

        const hasContents = (section.fixed_playlist?.contents?.length ?? 0) > 0;

        return (
            <div className={`pinned-section pt-3${hasContents ? '' : ' is-empty-playlist'}`}>
                <div
                    className="pinned-section-header screen-padding"
                    onClick={() => navigate("section/" + section.id)}
                >
                    <div className="pinned-section-banner bsf-9">
                        <div className="banner-clip">
                            {section.banner?.condensed && (
                                <img src={section.banner.condensed} alt="" />
                            )}
                        </div>
                        <span className="pinned-section-connector" aria-hidden="true" />
                    </div>
                    <div className="pinned-section-info">
                        <div className="title-container csf-2">
                            <KeyboardDoubleArrowRightRoundedIcon className="arrow-icon" />
                            <Text.h2 value={section.name} className="bold pinned-section-title" />
                        </div>
                        {section.description?.text && (
                            <span className="pinned-section-subtitle csf-6">
                                {section.description.text}
                            </span>
                        )}

                        <div className="pinned-section-metrics">
                            <Text.span
                                left={<SlowMotionVideoRounded />}
                                value={`${section.summary?.playlists ?? 0} playlist${(section.summary?.playlists ?? 0) !== 1 ? 's' : ''}`}
                                contrast={5}
                            />
                            <Text.span
                                left={<SkipNextRounded />}
                                value={`${section.summary?.contents?.amount ?? 0} vídeo${(section.summary?.contents?.amount ?? 0) !== 1 ? 's' : ''}`}
                                contrast={5}
                            />
                            <Text.span
                                left={<AccessTimeRounded />}
                                value={`${section.summary?.contents?.minutes ?? 0} minuto${(section.summary?.contents?.minutes ?? 0) !== 1 ? 's' : ''}`}
                                contrast={5}
                            />
                            <Button
                                size="micro"
                                color="inverse"
                                text="Ver todas as playlists"
                                startIcon={<KeyboardArrowDownRounded />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("section/" + section.id);
                                }}
                            />
                        </div>
                    </div>
                </div>

                <div className="pinned-section-playlist mt-2">
                    <Playlist
                        content={section.fixed_playlist}
                        course={section.id}
                        handleOpenContent={handleOpenContent}
                    />
                </div>
            </div>
        );
    }

    const carouselResponsive = {
        universal: {
            breakpoint: { max: 4000, min: 601 },
            items: 3,
            slidesToSlide: 2,
        },
        mobile: {
            breakpoint: { max: 600, min: 0 },
            items: 2,
            slidesToSlide: 1,
        },
    };

    return (
        <div ref={sectionRef} className="courses-section pt-1 pb-2">
            <div ref={headerRef} className="header py-1 csf-2 screen-padding">
                <SlowMotionVideoRounded className="section-icon" />
                <h3 className="bold">Sessões de conteúdo</h3>

                {info.isAdmin() && (
                    <Button
                        tooltip="Adicionar curso"
                        onClick={handleCreateNew}
                        icon={<AddRoundedIcon />}
                        color="overlay"
                    />
                )}
            </div>

            <SuggestionCard
                communityId={info?.id}
                headerRef={headerRef}
                sectionRef={sectionRef}
            />

            {loading ? (
                <CoursesBarSkeleton />
            ) : (
                <>
                    {pinnedSections.length > 0 && (
                        <div className="pinned-sections">
                            {pinnedSections.map((section) => (
                                <PinnedSection key={section.id} section={section} />
                            ))}
                        </div>
                    )}

                    {otherSections.length > 0 && (
                        <>
                            {pinnedSections.length > 0 && (
                                <div className="others-header screen-padding pt-4 pb-1 csf-2">
                                    <h4 className="bold">Outras sessões de conteúdo</h4>
                                </div>
                            )}

                            <div className="courses-list">
                                <div className="carousel-wrapper">
                                    <Carousel
                                        responsive={carouselResponsive}
                                        infinite={false}
                                        draggable={true}
                                        swipeable={true}
                                        keyBoardControl={true}
                                        containerClass="courses-list mt-1"
                                        itemClass="course-carousel-item"
                                        partialVisible={false}
                                        centerMode={false}
                                    >
                                        {otherSections.map((course, index) => (
                                            <div key={course.id ?? index} className={`${index === 0 ? 'pl-5' : ''}`}>
                                                <Course data={course} />
                                            </div>
                                        ))}
                                    </Carousel>
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}


function CoursesBarSkeleton() {
    return (
        <div className="courses-bar-skeleton">
            <div className="pinned-section pt-3">
                <div className="pinned-section-header screen-padding">
                    <div className="pinned-section-banner bsf-9">
                        <div className="banner-clip">
                            <Material.Skeleton variant="rectangular" width="100%" height="100%" style={{ borderRadius: 16 }} />
                        </div>
                    </div>
                    <div className="pinned-section-info">
                        <Material.Skeleton variant="text" width="60%" height={32} style={{ marginBottom: 6 }} />
                        <Material.Skeleton variant="text" width="80%" height={18} />
                    </div>
                </div>
                <div className="pinned-section-playlist screen-padding">
                    <Material.Skeleton variant="rectangular" width="100%" height={220} style={{ borderRadius: 16 }} />
                </div>
            </div>

            <div className="courses-list mt-3 screen-padding">
                <div className="course-skeleton-row">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="course-item">
                            <div className="banner-container bsf-9">
                                <Material.Skeleton variant="rectangular" width="100%" height="100%" style={{ borderRadius: 16 }} />
                            </div>
                            <div className="info mt-2">
                                <Material.Skeleton variant="text" width="70%" height={22} style={{ marginBottom: 4 }} />
                                <Material.Skeleton variant="text" width="90%" height={16} />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
